import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import { BASE_URL } from "../Apis/apiConfig";
import { AlertCircle, CheckCircle, Clock, FileText, RotateCw, Download, Eye, Calendar, X, Image, File, Music, Film, ZoomIn, ZoomOut, Expand, XCircle } from "lucide-react";
import log from "../image/compaints.mp4";
import '../Style/ComplaintsRegister.css';
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";


export default function ComplaintsRegister() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [fileSrcMap, setFileSrcMap] = useState({});
  const createdObjectUrls = useRef([]);
  const societyName = localStorage.getItem("society") || "";
  const token = localStorage.getItem("token") || "";
  const currentUser =
    localStorage.getItem("memberName") ||
    localStorage.getItem("adminName") ||
    "admin";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const pageSizeOptions = [20, 10, 20, "All"];
  const [resolvingMap, setResolvingMap] = useState({});
  const [itemErrorMap, setItemErrorMap] = useState({});
  const [remarkMap, setRemarkMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [attachmentZoom, setAttachmentZoom] = useState(1);
  const [showAllCards, setShowAllCards] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [liveNotifications, setLiveNotifications] = useState([]);
  const [countMap, setCountMap] = useState({});
  const [seenIds, setSeenIds] = useState(() => {
  const saved = localStorage.getItem("seenComplaintIds");
    return saved ? JSON.parse(saved) : [];
  });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const totalItems = complaints.length;
  const effectivePageSize = pageSize === "All" ? totalItems || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(totalItems / effectivePageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [totalPages, pageSize]);

  const unseenCount = liveNotifications.filter(
    (n) => !seenIds.includes(n.id)
  ).length;


  useEffect(() => {
    localStorage.setItem("seenCount", 9999);
  }, []);

  useEffect(() => {
    if (!complaints.length) return;

    const updatedCounts = {};

    complaints.forEach((c) => {
      if (!c.unitNo) return;

      const falseRemarkCount = (c.files || []).filter(
        (f) => f.remark === false
      ).length;

      if (!updatedCounts[c.unitNo]) {
        updatedCounts[c.unitNo] = 0;
      }

      updatedCounts[c.unitNo] += falseRemarkCount;
    });

    setCountMap(updatedCounts);
  }, [complaints]);

  const getFalseRemarkCount = (complaint) => {
    return (complaint.files || []).filter(
      (f) => f.remark === false
    ).length;
  };

  useEffect(() => {
    let mounted = true;
    loadComplaints("all")
    if (societyName) loadComplaints("all");
    else setError("No society name found in localStorage (key: 'society').");

    return () => {
      mounted = false;
      createdObjectUrls.current.forEach((u) => URL.revokeObjectURL(u));
      createdObjectUrls.current = [];
      setFileSrcMap({});
    };
  }, [societyName, token]);

  async function fetchAllFilesAsBlobs(normalizedComplaints) {
    const toFetch = [];
    normalizedComplaints.forEach((c) => {
      (c.files || []).forEach((f) => {
        if (f.fileId && f.streamUrl)
          toFetch.push({ id: f.fileId, url: f.streamUrl, type: f.fileType });
      });
    });

    const unique = [];
    const seen = new Set();
    for (const t of toFetch) {
      if (!seen.has(t.id)) {
        seen.add(t.id);
        unique.push(t);
      }
    }

    const concurrency = 6;
    let idx = 0;

    async function worker() {
      while (idx < unique.length) {
        const current = unique[idx++];
        try {
          const res = await axios.get(current.url, {
            responseType: "blob",
            headers: { Authorization: `Bearer ${token}` },
          });
          const blob = res.data;
          const blobUrl = URL.createObjectURL(blob);
          createdObjectUrls.current.push(blobUrl);
          setFileSrcMap((prev) => ({ ...prev, [current.id]: blobUrl }));
        } catch (err) {
          console.warn(
            "Failed to fetch protected file blob:",
            current.url,
            err?.message || err,
          );
          setFileSrcMap((prev) => ({ ...prev, [current.id]: current.url }));
        }
      }
    }

    const workers = [];
    for (let w = 0; w < concurrency; w++) workers.push(worker());
    await Promise.all(workers);
  }

  async function loadComplaints(type) {
    let mounted = true;
    setLoading(true);
    setError("");
    try {
      const url = `${BASE_URL}/complaint/get-complaints-by-society-name`;
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const resp = await axios.post(url, null, {
        headers,
        params: { societyName, type },
      });

      if (!mounted) return;

      const payload = resp?.data;
      const arr = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Object.values(payload).find((v) => Array.isArray(v)) || [];

      const normalized = arr.map((c, idx) => {
        const complaintId =
          c.complaintDTOId ?? c.complaintId ?? c.id ?? idx + 1;
        const files = Array.isArray(c.files) ? c.files : [];

        const filesNormalized = files.map((f) => {
          const fileId = f.fileId ?? f.id ?? null;
          const streamUrlProvided = f.streamUrl ?? null;
          const builtStreamUrl = fileId
            ? `${BASE_URL}/complaint/get-image-by-url?id=${fileId}`
            : null;

          return {
            fileId,
            fileName: f.fileName ?? f.file_name ?? "",
            fileType: f.fileType ?? f.file_type ?? "",
            fileUrl: f.fileUrl ?? f.fileName ?? "",
            remark: f.remark ?? f.resolve ?? false,

            streamUrl: streamUrlProvided || builtStreamUrl,
            size: f.size || "Unknown",
          };
        });

        return {
          complaintId,
          subject: c.subject ?? c.title ?? `Complaint #${complaintId}`,
          complaintDetails: c.complaintDetails ?? c.description ?? "",
          date: c.date ?? c.createdAt ?? null,
          memberName: c.memberName ?? c.reportedBy ?? "",
          unitNo: c.unitNo ?? c.unit ?? "",
          status: c.status ?? "Pending",
          priority: c.priority ?? "medium",
          category: c.category ?? "General",
          files: filesNormalized,
          remark: c.remark ?? false,
        };
      });

      if (!mounted) return;
      setComplaints(normalized);
      setPage(1);

      if (token) {
        await fetchAllFilesAsBlobs(normalized);
      } else {
        const map = {};
        normalized.forEach((c) => {
          (c.files || []).forEach((f) => {
            if (f.fileId && f.streamUrl) map[f.fileId] = f.streamUrl;
          });
        });
        if (mounted) setFileSrcMap(map);
      }
    } catch (err) {
      console.error("Failed to load complaints:", err);
      let msg = "Failed to load complaints";
      if (err?.response) {
        if (err.response.status === 401 || err.response.status === 403)
          msg = "Unauthorized. Please login.";
        else
          msg =
            err.response?.data?.message ||
            `Server error ${err.response.status}`;
      } else if (err?.message && err.message.includes("Network Error")) {
        msg = "Network error — check server and CORS.";
      } else msg = err?.message ?? msg;
      if (mounted) setError(msg);
    } finally {
      if (mounted) setLoading(false);
    }
  }

  const isImage = (type) =>
    typeof type === "string" && type.startsWith("image/");
  const isAudio = (type) =>
    typeof type === "string" && type.startsWith("audio/");
  const isVideo = (type) =>
    typeof type === "string" && type.startsWith("video/");
  const isPDF = (type) => typeof type === "string" && type.includes("pdf");
  const isDocument = (type) =>
    typeof type === "string" &&
    (type.includes("word") ||
      type.includes("document") ||
      type.includes("msword") ||
      type.includes("excel") ||
      type.includes("spreadsheet") ||
      type.includes("powerpoint") ||
      type.includes("presentation"));

  const getFileIcon = (fileType) => {
    if (isImage(fileType)) return <Image size={20} />;
    if (isPDF(fileType)) return <FileText size={20} />;
    if (isDocument(fileType)) return <File size={20} />;
    if (isAudio(fileType)) return <Music size={20} />;
    if (isVideo(fileType)) return <Film size={20} />;
    return <File size={20} />;
  };

  const filteredComplaints = complaints.filter((complaint) => {
    if (
      searchTerm &&
      !complaint.subject.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !complaint.complaintDetails
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) &&
      !complaint.memberName.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }
    if (
      filterStatus !== "all" &&
      complaint.status.toLowerCase() !== filterStatus.toLowerCase()
    ) {
      return false;
    }

    return true;
  });

  const getPaginatedComplaints = () => {
    const filtered = filteredComplaints;
    if (pageSize === "All") return filtered;
    const size = pageSize;
    const start = (page - 1) * size;
    return filtered.slice(start, start + size);
  };

  const displayedComplaints = showAllCards
    ? filteredComplaints
    : getPaginatedComplaints();

  const pageNumbers = (() => {
    const maxButtons = 7;
    const pages = [];
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    let start = Math.max(1, page - 3);
    let end = Math.min(totalPages, page + 3);
    if (page <= 4) {
      start = 1;
      end = Math.min(totalPages, maxButtons);
    }
    if (page > totalPages - 4) {
      end = totalPages;
      start = Math.max(1, totalPages - (maxButtons - 1));
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  })();

  const handleChangePageSize = (newSize) => {
    setPageSize(newSize === "All" ? "All" : Number(newSize));
    setPage(1);
  };

  const handleViewDetails = (complaint) => {
    setSelectedComplaint(complaint);
    setShowDetailModal(true);
  };

  const handleViewAttachment = (file, e) => {
    e.stopPropagation();
    setSelectedAttachment(file);
    setShowAttachmentViewer(true);
    setAttachmentZoom(1);
  };

  const handleDownloadAttachment = (file, e) => {
    e.stopPropagation();
    const src = fileSrcMap[file.fileId]; 
    if (src) {
      const link = document.createElement("a");
      link.href = src;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  const markAsResolved = async (
    complaintId,
    adminRemark = "",
    resolveValue = true,
  ) => {
    if (!complaintId) return;

    const complaint = complaints.find((c) => c.complaintId === complaintId);
    let fileId = 0;
    if (complaint && Array.isArray(complaint.files) && complaint.files.length > 0) {
      const firstFile =
        complaint.files.find((f) => f.fileId) || complaint.files[0];

      fileId = firstFile?.fileId ?? 0;
    }

    setItemErrorMap((prev) => ({ ...prev, [complaintId]: "" }));
    setResolvingMap((prev) => ({ ...prev, [complaintId]: true }));

    const previousComplaints = complaints;
    setComplaints((prev) =>
      prev.map((c) =>
        c.complaintId === complaintId
          ? { ...c, status: resolveValue ? "Resolved" : c.status }
          : c,
      ),
    );

    try {
      const url = `${BASE_URL}/complaint/admin-remark`;

      const params = {
        resolve: resolveValue,
        societyName: societyName,
        urlId: complaintId,
        fileId: fileId ?? 0,
        remark: adminRemark?.trim() || "",
      };

      const headers = {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const resp = await axios.post(url, null, {
        headers,
        params,
        timeout: 60000,
      });

      if (resp && resp.status >= 200 && resp.status < 300) {
        setComplaints((prev) =>
          prev.map((c) =>
            c.complaintId === complaintId
              ? { ...c, status: "Resolved" }
              : c,
          ),
        );
      } else {
        throw new Error(`Server returned ${resp?.status}`);
      }
    } catch (err) {
      setComplaints(previousComplaints);

      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to mark resolved";

      setItemErrorMap((prev) => ({
        ...prev,
        [complaintId]: msg,
      }));
    } finally {
      setResolvingMap((prev) => ({
        ...prev,
        [complaintId]: false,
      }));
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    const client = new Client({
      webSocketFactory: () => new SockJS(`${BASE_URL}/ws-chat`),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnectDelay: 5000,

      onConnect: () => {
        client.subscribe("/topic/notifications", (message) => {
          const data = JSON.parse(message.body);
          if (data.type === "COMPLAINT") {
            const newComplaint = {
              complaintId: data.id,
              subject: data.subject,
              complaintDetails: data.message,
              date: data.date,
              memberName: data.memberName,
              unitNo: data.unitNo,
              status: "Pending",
              files: []
            };

            setComplaints((prev) => [newComplaint, ...prev]);
            setLiveNotifications((prev) => [newComplaint, ...prev]);
          }
        });
      },
    });

    client.activate();
    return () => client.deactivate();
  }, []);

  const getAdminCount = async (unitNo) => {
    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society");

      const res = await fetch(
        `${BASE_URL}/complaint/get-count-admin?societyName=${encodeURIComponent(
          societyName
        )}&unitNo=${encodeURIComponent(unitNo)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, 
          },
        }
      );

      if (!res.ok) throw new Error("Failed to fetch count");

      const count = await res.json();
      return count;

    } catch (err) {
      console.error("Error fetching count:", err);
      return 0;
    }
  };

  const handleCardClick = (c) => {
    handleViewDetails(c);
    if (!seenIds.includes(c.complaintId)) {
      const updated = [...seenIds, c.complaintId];
      setSeenIds(updated);
      localStorage.setItem("seenComplaintIds", JSON.stringify(updated));
    }
  };
  
  const renderStatusBadge = (c) => {
    if (c.remark === true) {
      return (
        <span className="status-badge solved">
          <CheckCircle size={14} /> Resolved
        </span>
      );
    }

    return (
      <span className="status-badge pending">
        <Clock size={14} /> Pending
      </span>
    );
  };

  const VideoPlayer = ({ src, type, fileName }) => {
    const videoRef = useRef(null);
    const [isPlayable, setIsPlayable] = useState(true);
    const [isPortrait, setIsPortrait] = useState(false);
    const [rotations, setRotations] = useState(0);

    useEffect(() => {
      setRotations(0);
      setIsPlayable(true);
      setIsPortrait(false);
    }, [src]);

    const onLoadedMetadata = () => {
      const v = videoRef.current;
      if (!v) return;
      try {
        const vw = v.videoWidth;
        const vh = v.videoHeight;
        if (vw && vh) {
          setIsPortrait(vh > vw);
        }
      } catch (e) {
      }
    };

    const onError = () => {
      setIsPlayable(false);
    };

    const rotate = () => {
      setRotations((r) => (r + 1) % 4);
    };

    const transformStyle = {
      transform: `rotate(${rotations * 90}deg)`,
      transition: "transform 250ms",
    };
    const containerStyle = isPortrait
      ? {
        width: 180,
        height: 260,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }
      : {
        width: 220,
        height: 140,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      };

    const videoStyle = {
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "cover",
      ...transformStyle,
    };

    const playableTypes = [
      "video/mp4",
      "video/webm",
      "video/ogg",
      "video/quicktime",
      "video/x-ms-wmv",
    ];
    const canAttemptPlay =
      isVideo(type) ||
      (typeof type === "string" && playableTypes.includes(type));

    return (
      <div style={containerStyle}>
        {src && canAttemptPlay ? (
          <>
            <video
              ref={videoRef}
              controls
              playsInline
              preload="metadata"
              onLoadedMetadata={onLoadedMetadata}
              onError={onError}
              style={videoStyle}
            >
              <source src={src} type={type || "video/mp4"} />
              Your browser does not support the video tag.
            </video>

            <div
              style={{
                position: "absolute",
                marginTop: isPortrait ? 200 : 120,
              }}
            >
              <button
                type="button"
                className="rotate-btn"
                onClick={rotate}
                title="Rotate video 90°"
              >
                <RotateCw size={14} /> Rotate
              </button>
            </div>
          </>
        ) : src ? (
          <div style={{ textAlign: "center", padding: 8 }}>
            <div style={{ fontSize: 13, marginBottom: 6 }}>{fileName}</div>
            <a href={src} target="_blank" rel="noopener noreferrer">
              Download / Open file
            </a>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "#666" }}>
            No preview available
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app-container">

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />
          <div className="complaints-container">
          
            <div className="left-panel">
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  marginBottom: "10px",
                  padding: "4px 6px"
                }}
              >
                <button
                  onClick={() => loadComplaints("all")}
                  onMouseEnter={() => setHovered("all")}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: "12px",
                    border: "none",
                    cursor: "pointer",
                    background:
                      filterStatus === "all"
                        ? "#1e293b"
                        : hovered === "all"
                          ? "#cbd5f5"
                          : "#e2e8f0",
                    color: filterStatus === "all" ? "#fff" : "#000",
                    fontSize: "12px",
                    fontWeight: 500
                  }}
                >
                  All
                </button>

                <button
                  onClick={() => loadComplaints("pending")}
                  onMouseEnter={() => setHovered("pending")}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: "12px",
                    border: "none",
                    cursor: "pointer",
                    background:
                      filterStatus === "pending"
                        ? "#f59e0b"
                        : hovered === "pending"
                          ? "#facc15"
                          : "#e2e8f0",
                    color: filterStatus === "pending" ? "#fff" : "#000",
                    fontSize: "12px",
                    fontWeight: 500
                  }}
                >
                  {selectedComplaint?.files?.some(f => f.remark === true)
                    ? "Solved"
                    : "Pending"}
                </button>

                <button
                  onClick={() => loadComplaints("resolved")}
                  onMouseEnter={() => setHovered("resolved")}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: "12px",
                    border: "none",
                    cursor: "pointer",
                    background:
                      filterStatus === "resolved"
                        ? "#10b981"
                        : hovered === "resolved"
                          ? "#22c55e"
                          : "#e2e8f0",
                    color: filterStatus === "resolved" ? "#fff" : "#000",
                    fontSize: "12px",
                    fontWeight: 500
                  }}
                >
                  Resolved
                </button>
              </div>
              <div className="notification-scroll">
                {displayedComplaints.map((c) => {
                  const hasNotification = liveNotifications.some(
                    (n) => n.complaintId === c.complaintId
                  );
                  const hasFiles = (c.files || []).length > 0;
                  const isSeen = c.remark === true;
                  const isUnseen = c.remark === false;

                  return (
                    <div
                      key={c.complaintId}
                      className={`notification-card ${selectedComplaint?.complaintId === c.complaintId ? "active" : ""
                        }`}
                      onClick={() => handleCardClick(c)}
                      style={{ position: "relative" }}
                    >
                     
                      {isUnseen && (
                        <span
                          style={{
                            position: "absolute",
                            top: "-6px",
                            right: "-6px",
                            minWidth: "18px",
                            height: "18px",
                            backgroundColor: "red", // ✅ FIXED
                            color: "#fff",
                            borderRadius: "50%",
                            fontSize: "11px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            padding: "2px"
                          }}
                        >
                          {getFalseRemarkCount(c)} 
                        </span>
                      )}

                      <div className="card-member" style={{ fontSize: "13px", color: "#64748b", marginTop: "6px" }}>
                        <div className="card-member">
                          <div className="card-member card-row">
                            <div className="card-subject" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {c.subject}

                              {!c.remark && 1}
                            </div>
                            <div className="card-flat">Flat {c.unitNo}</div>
                          </div>

                          <div className="card-meta-line">
                            {c.memberName} <span className="card-dot">•</span> Flat {c.unitNo}
                          </div>
                        </div>
                      </div>

                      <div className="card-date">
                        <Calendar size={14} />
                        {c.date ? new Date(c.date).toLocaleDateString() : "N/A"}
                      </div>
                    </div>
                  );
                })}
                <div style={{ textAlign: "center", marginTop: "8px" }}>
                  {filteredComplaints.length > pageSize && (
                    <button
                      onClick={() => setShowAllCards(!showAllCards)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "8px",
                        border: "none",
                        background: "#1e293b",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 500
                      }}
                    >
                      {showAllCards ? "See Less" : "See More"}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="right-panel">
              <video
                src={log}
                autoPlay
                loop
                muted
                playsInline
                className="right-panel-media"
              />
              <div style={{ marginTop: "20px", display: "flex", gap: "18px" }}>
               
                <div

                  style={{
                    width: "140px",
                    padding: "18px",
                    borderRadius: "16px",
                    background: "#A8BCA8",
                    border: "1px solid #dcfce7",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                    transition: "0.25s"
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-3px)")}
                  onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0px)")}
                >
                  <CheckCircle size={26} color="#10b981" />
                  <span style={{ color: "#065f46", fontWeight: 600 }}>Approve</span>
                </div>
                <div

                  style={{
                    width: "140px",
                    padding: "18px",
                    borderRadius: "16px",
                    background: "#A8BCA8",
                    border: "1px solid #dcfce7",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                    transition: "0.25s"
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-3px)")}
                  onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0px)")}
                >
                  <XCircle size={26} color="#ef4444" />
                  <span style={{ color: "#991b1b", fontWeight: 600 }}>Reject</span>
                </div>
              </div>
            </div>

            {showDetailModal && selectedComplaint && (
              <div
                className="modal-overlay"
                onClick={() => setShowDetailModal(false)}
              >
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-header">
                    <h3 className="modal-title">Complaint Details</h3>
                    <button
                      className="modal-close"
                      onClick={() => setShowDetailModal(false)}
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="modal-meta">
                      <div className="modal-meta-item">
                        <span className="modal-meta-label">Complaint ID</span>
                        <span className="modal-meta-value">
                          #{selectedComplaint.complaintId}
                        </span>
                      </div>
                      <div className="modal-meta-item">
                        <span className="modal-meta-label">Submitted By</span>
                        <span className="modal-meta-value">
                          {selectedComplaint.memberName}
                        </span>
                      </div>
                      <div className="modal-meta-item">
                        <span className="modal-meta-label">Unit No</span>
                        <span className="modal-meta-value">
                          {selectedComplaint.unitNo}
                        </span>
                      </div>
                      <div className="modal-meta-item">
                        <span className="modal-meta-label">Date</span>
                        <span className="modal-meta-value">
                          {selectedComplaint.date
                            ? new Date(selectedComplaint.date).toLocaleString()
                            : "N/A"}
                        </span>
                      </div>
                      <div className="modal-meta-item">
                        <span className="modal-meta-label">Status</span>
                        <span className="modal-meta-value">
                          {renderStatusBadge(selectedComplaint)}
                        </span>
                      </div>
                      <div className="modal-meta-item">
                        <span className="modal-meta-label">Category</span>
                        <span className="modal-meta-value">
                          {selectedComplaint.category}
                        </span>
                      </div>
                    </div>

                    <div className="modal-description">
                      <h4>Description</h4>
                      <div className="modal-description-text">
                        {selectedComplaint.complaintDetails}
                      </div>
                    </div>

                    {selectedComplaint.files &&
                      selectedComplaint.files.length > 0 && (
                        <div className="modal-attachments">
                          <h4>Attachments ({selectedComplaint.files.length})</h4>
                          <div className="attachments-grid">
                            {selectedComplaint.files.map((file) => {
                              const src =
                                fileSrcMap[file.fileId] || file.streamUrl;
                              return (
                                <div
                                  key={file.fileId}
                                  className="attachment-preview"
                                  onClick={(e) => handleViewAttachment(file, e)}
                                >
                                  {isImage(file.fileType) ? (
                                    <>
                                      <img
                                        src={src}
                                        alt={file.fileName}
                                        className="attachment-preview-image"
                                      />
                                      <div className="attachment-overlay">
                                        {file.fileName}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="attachment-file">
                                        {getFileIcon(file.fileType)}
                                        <div className="attachment-file-name">
                                          {file.fileName}
                                        </div>
                                      </div>
                                      <div className="attachment-overlay">
                                        {file.fileName}
                                      </div>
                                    </>
                                  )}
                                  <div className="attachment-actions">
                                    <button
                                      className="attachment-action-btn"
                                      onClick={(e) =>
                                        handleViewAttachment(file, e)
                                      }
                                      title="View"
                                    >
                                      <Eye size={12} />
                                    </button>
                                    <button
                                      className="attachment-action-btn"
                                      onClick={(e) =>
                                        handleDownloadAttachment(file, e)
                                      }
                                      title="Download"
                                    >
                                      <Download size={12} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                  </div>

                  <div className="modal-footer">
                    <div className="modal-actions">
                      <div className="remark-section">
                        <input
                          type="text"
                          placeholder="Add admin remark..."
                          className="remark-input"
                          value={remarkMap[selectedComplaint.complaintId] || ""}
                          onChange={(e) =>
                            setRemarkMap((prev) => ({
                              ...prev,
                              [selectedComplaint.complaintId]: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="modal-action-buttons">
                        <button
                          className="btn-secondary"
                          onClick={() => setShowDetailModal(false)}
                        >
                          <X size={16} />
                          Close
                        </button>
                        {selectedComplaint.status !== "Resolved" && (
                          <button
                            className="btn-primary"
                            onClick={() => {
                              markAsResolved(
                                selectedComplaint.complaintId,
                                remarkMap[selectedComplaint.complaintId] || "",
                                true,
                              );

                              setTimeout(() => {
                                if (
                                  !itemErrorMap[selectedComplaint.complaintId]
                                ) {
                                  setShowDetailModal(false);
                                }
                              }, 1000);
                            }}
                            disabled={
                              !!resolvingMap[selectedComplaint.complaintId]
                            }
                          >
                            <CheckCircle size={16} />
                            {resolvingMap[selectedComplaint.complaintId]
                              ? "Processing..."
                              : "Mark as Resolved"}
                          </button>
                        )}
                      </div>
                    </div>
                    {itemErrorMap[selectedComplaint.complaintId] && (
                      <div className="error-state" style={{ marginTop: "12px" }}>
                        <AlertCircle size={16} />
                        {itemErrorMap[selectedComplaint.complaintId]}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {showAttachmentViewer && selectedAttachment && (
              <div
                className="attachment-viewer-modal"
                onClick={() => setShowAttachmentViewer(false)}
              >
                <div
                  className="attachment-viewer-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="attachment-viewer-header">
                    <h3 className="attachment-viewer-title">
                      {getFileIcon(selectedAttachment.fileType)}
                      {selectedAttachment.fileName}
                    </h3>
                    <button
                      className="attachment-viewer-close"
                      onClick={() => setShowAttachmentViewer(false)}
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="attachment-viewer-body">
                    {isImage(selectedAttachment.fileType) && (
                      <img
                        src={fileSrcMap[selectedAttachment.fileId] || selectedAttachment.streamUrl}
                        alt={selectedAttachment.fileName}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "400px",
                          borderRadius: "10px"
                        }}
                      />
                    )}

                    {isVideo(selectedAttachment.fileType) && (
                      <video
                        controls
                        style={{
                          maxWidth: "100%",
                          maxHeight: "400px",
                          borderRadius: "10px"
                        }}
                      >
                        <source
                          src={fileSrcMap[selectedAttachment.fileId] || selectedAttachment.streamUrl}
                          type={selectedAttachment.fileType}
                        />
                        Your browser does not support video.
                      </video>
                    )}

                    {isAudio(selectedAttachment.fileType) && (
                      <audio controls style={{ width: "100%", marginTop: "20px" }}>
                        <source
                          src={fileSrcMap[selectedAttachment.fileId] || selectedAttachment.streamUrl}
                          type={selectedAttachment.fileType}
                        />
                        Your browser does not support audio.
                      </audio>
                    )}

                    {!isImage(selectedAttachment.fileType) &&
                      !isVideo(selectedAttachment.fileType) &&
                      !isAudio(selectedAttachment.fileType) && (
                        <div style={{ textAlign: "center" }}>
                          <p>No preview available</p>
                        </div>
                      )}
                  </div>

                  <div className="attachment-viewer-actions">
                    <div className="viewer-controls">
                      {isImage(selectedAttachment.fileType) && (
                        <>
                          <button
                            className="viewer-control-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachmentZoom(
                                Math.min(3, attachmentZoom + 0.25),
                              );
                            }}
                          >
                            <ZoomIn size={14} />
                            Zoom In
                          </button>
                          <button
                            className="viewer-control-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachmentZoom(
                                Math.max(0.5, attachmentZoom - 0.25),
                              );
                            }}
                          >
                            <ZoomOut size={14} />
                            Zoom Out
                          </button>
                          <button
                            className="viewer-control-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachmentZoom(1);
                            }}
                          >
                            <Expand size={14} />
                            Reset
                          </button>
                        </>
                      )}
                    </div>

                    <div className="viewer-info">
                      <div className="viewer-file-name">
                        {selectedAttachment.fileName}
                      </div>
                      <div className="viewer-file-info">
                        {selectedAttachment.fileType} •{" "}
                        {selectedAttachment.size || "Size unknown"}
                      </div>
                    </div>

                    <button
                      className="viewer-control-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadAttachment(selectedAttachment, e);
                      }}
                    >
                      <Download size={14} />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

        </main>
      </div>
    </div>
  );
}
